/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useState, useEffect, Fragment, useCallback } from 'react';
import axiosCall from '../services/axios';
import Style from '../css/MyProfile.module.css';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  IconButton,
  InputAdornment,
  FormHelperText,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EnvelopeWithBox from '../components/EnvelopeWithBox';
import useGetBorrowerWebUser from '../common/hooks/useGetBorrowerWebUser';
import LoadingModal from '../components/modals/LoadingModal';
import SelectModal from '../components/modals/SelectModal';
import useAbortController from '../common/hooks/useAbortController';
import ButtonBorder from '../components/ButtonBorder';
import * as EmailValidator from 'email-validator';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useAuthContext } from '../contexts/AuthContext';
import ButtonBorderWrapper from '../components/ButtonBorderWrapper';
import { Lock } from '@mui/icons-material';
import { SelectType } from 'src/types/SelectType';
import { useBrwrContext } from '../contexts/BorrowerContext';
import { useLoanSearchQuery } from '../common/hooks/useLoanSearchQuery';

function MyProfilePage() {
  const { borrowerData, reloadBorrowerData } = useBrwrContext();
  const [loading, setLoading] = useState(false);
  const [openSelectLoanModal, setOpenSelectLoanModal] = useState(false);
  const [modalRows, setModalRows] = useState([] as SelectType[]);
  const [currentEmailAddress, setCurrentEmailAddress] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentPasswordError, setCurrentPasswordError] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [confirmNewEmailAddress, setConfirmNewEmailAddress] = useState('');
  const [newEmailError, setNewEmailError] = useState(false);
  const [newEmailErrorMessage, setNewEmailErrorMessage] = useState('');
  const [confirmNewEmailError, setConfirmNewEmailError] = useState(false);
  const [confirmNewEmailErrorMessage, setConfirmNewEmailErrorMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState(false);
  const [newPasswordErrorMessage, setNewPasswordErrorMessage] = useState('');
  const [confirmNewPasswordError, setConfirmNewPasswordError] = useState(false);
  const [confirmNewPasswordErrorMessage, setConfirmNewPasswordErrorMessage] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const { email, token, updateUser } = useAuthContext();

  const webUserUrl = process.env.REACT_APP_WEB_USER;
  const modalTitle = 'Which loans should we update the email address on?';
  const modalLoanColumnTitle = 'Loan Number';
  const emailErrorMessage = 'This field must match the New Email field.';
  const emailValidationErrorMessage = 'This is not a valid email address.';
  const passwordErrorMessage = 'This field must match the New Password field.';
  const passwordValidationErrorMessage = 'Password must follow below rules.';
  const { firstName, lastName, webUserId, webUserInfoErrorMessage, webUserInfoLoading } = useGetBorrowerWebUser(email);

  const { get } = useAbortController();
  const { enqueueSnackbar } = useSnackbar();

  const handleClickShowNewPassword = () => {
    setShowNewPassword((prev) => !prev);
  };

  const handleClickShowConfirmNewPassword = () => {
    setShowConfirmNewPassword((prev) => !prev);
  };

  const handleClickShowCurrentPassword = () => {
    setShowCurrentPassword((prev) => !prev);
  };

  const handleModalUpdateRowChecked = (index: number) => {
    modalRows[index].selected = !modalRows[index].selected;
    setModalRows(modalRows);
  };

  const handleModalCheckAll = (checked: boolean) => {
    const row: SelectType[] = modalRows.map((item: SelectType) => ({
      selected: checked,
      value: item.value,
    }));
    setModalRows(row);
  };

  const handleModalCancel = () => {
    setOpenSelectLoanModal(false);
  };

  const handleOpenModal = () => {
    if (newEmailAddress.length !== 0 && modalRows.length !== 0) {
      setOpenSelectLoanModal(true);
    } else {
      handleSubmitClicked();
    }
  };

  const handleSubmitClicked = async () => {
    setSubmitLoading(true);
    setOpenSelectLoanModal(false);

    if (newPassword.length !== 0) {
      await savePassword();
    }
    if (newEmailAddress.length !== 0) {
      await saveEmail();
    }

    setSubmitLoading(false);
  };

  const validateEmail = useCallback(() => {
    let newHasError = false;
    let confirmHasError = false;

    if (newEmailAddress !== '') {
      if (!EmailValidator.validate(newEmailAddress)) {
        setNewEmailError(true);
        setNewEmailErrorMessage(emailValidationErrorMessage);
        newHasError = true;
      }
    }

    if (confirmNewEmailAddress !== '') {
      if (!EmailValidator.validate(confirmNewEmailAddress)) {
        setConfirmNewEmailError(true);
        setConfirmNewEmailErrorMessage(emailValidationErrorMessage);
        confirmHasError = true;
      }
    }

    if (newEmailAddress !== confirmNewEmailAddress) {
      if (!newHasError) {
        setNewEmailError(true);
        setNewEmailErrorMessage(' ');
        newHasError = true;
      }

      if (!confirmHasError) {
        setConfirmNewEmailError(true);
        setConfirmNewEmailErrorMessage(emailErrorMessage);
        confirmHasError = true;
      }
    }

    if (!newHasError && !confirmHasError) {
      setNewEmailError(false);
      setNewEmailErrorMessage('');
      setConfirmNewEmailError(false);
      setConfirmNewEmailErrorMessage('');
    }
  }, [newEmailAddress, confirmNewEmailAddress, emailErrorMessage, emailValidationErrorMessage]);

  const validatePassword = useCallback(() => {
    //The below regex requires one capital letter, one number, one special character, and a minimum of eight characters
    const passwordValidationRegex = new RegExp(
      '(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*?&])[0-9a-zA-Z@$!%*?&]{8,}',
    );
    const validate = (value: string) => {
      const matches = value.match(passwordValidationRegex);
      return matches !== undefined && matches !== null && matches?.includes(value);
    };

    if (newPassword.length !== 0) {
      //check to see if the new password field is valid
      if (validate(newPassword)) {
        setNewPasswordError(false);
        setNewPasswordErrorMessage('');
      } else {
        setNewPasswordError(true);
        setNewPasswordErrorMessage(passwordValidationErrorMessage);
      }
    }

    if (confirmNewPassword.length !== 0) {
      //check to see if the confirm new password field is valid
      if (confirmNewPassword.length === 0 || validate(confirmNewPassword)) {
        setConfirmNewPasswordError(false);
        setConfirmNewPasswordErrorMessage('');
      } else {
        setConfirmNewPasswordError(true);
        setConfirmNewPasswordErrorMessage(passwordValidationErrorMessage);
      }
    }

    // Check to see if the two password fields are the same
    if (newPassword !== confirmNewPassword) {
      setNewPasswordError(true);
      setConfirmNewPasswordError(true);
      setNewPasswordErrorMessage(' ');
      setConfirmNewPasswordErrorMessage(passwordErrorMessage);
    }
    //Current Password must be set to update password
    if ((newPassword.length !== 0 || confirmNewPassword.length !== 0) && currentPassword.length === 0) {
      setCurrentPasswordError(true);
    } else {
      setCurrentPasswordError(false);
    }

    if (newPassword.length === 0 && confirmNewPassword.length === 0) {
      //If both boxes are empty then there are no errors
      setNewPasswordError(false);
      setConfirmNewPasswordError(false);
      setConfirmNewPasswordErrorMessage('');
    }
  }, [newPassword, confirmNewPassword, passwordValidationErrorMessage, passwordErrorMessage, currentPassword]);

  const checkForErrors = useCallback(() => {
    //make sure there are no errors present
    let disable = newEmailError || newPasswordError || confirmNewPasswordError || currentPasswordError;
    //make sure that the fields are filled out
    disable =
      disable ||
      (newEmailAddress.length === 0 &&
        confirmNewEmailAddress.length === 0 &&
        newPassword.length === 0 &&
        confirmNewPassword.length === 0) ||
      newEmailAddress === currentEmailAddress;
    setDisableSubmit(disable);
  }, [
    newEmailError,
    newPasswordError,
    confirmNewPasswordError,
    newEmailAddress,
    confirmNewEmailAddress,
    newPassword,
    confirmNewPassword,
    currentEmailAddress,
    currentPasswordError,
  ]);

  const savePassword = async () => {
    if (!token) return;

    const url = process.env.REACT_APP_WEB_USER_NEW_PASSWORD;

    const controller = get();
    const config = { signal: controller.signal };

    const formData = {
      email: currentEmailAddress,
      password: currentPassword,
      newPassword: newPassword,
      userId: webUserId,
    };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    setSubmitLoading(true);
    try {
      const response = await axiosCall({ url: url, method: 'post', data: formData }, config, headers);
      if (response.success) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        enqueueSnackbar(response.message, { variant: 'success' });
      } else {
        enqueueSnackbar(response.error, { variant: 'error' });
      }
    } catch (error) {
      console.error('Unable to save new Password: ' + error);
      enqueueSnackbar('Unable to save new Password.', { variant: 'error' });
    }
  };

  const saveEmail = async () => {
    if (!token) return;

    const url = webUserUrl + webUserId;

    const borrowers = Object.values(borrowerData);
    if (borrowers.length !== 0) {
      for await (const row of modalRows) {
        if (!row.selected) continue;
        //for all loans that include the borrower's email and were selected in the popup update to the new email address
        if (row.selected === true) {
          const filteredBorrowers = borrowers.filter((item) => item.emailAddress === currentEmailAddress);
          for await (const borrower of filteredBorrowers) {
            //update borrower
            await updateBorrowerEmail(row.value, borrower.borrowerNumber, newEmailAddress);
          }
        }
      }
    }

    const controller = get();
    const config = { signal: controller.signal };

    const formData = { email: newEmailAddress };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    setSubmitLoading(true);
    try {
      const response = await axiosCall({ url: url, method: 'post', data: formData }, config, headers);
      const responseEmail = response.email;
      if (responseEmail) {
        setCurrentEmailAddress(responseEmail);
        if (responseEmail === newEmailAddress) {
          updateUser({
            email: responseEmail,
          });
          setNewEmailAddress('');
          setConfirmNewEmailAddress('');
          enqueueSnackbar('Email Address was updated.', { variant: 'success' });
        } else {
          enqueueSnackbar('Unable to Save Email Address.', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Unable to save new Email Address: ' + error);
      enqueueSnackbar('Unable to save new Email Address.', { variant: 'error' });
    } finally {
      reloadBorrowerData();
    }
  };

  // Does this need to run on every render?!? If so, look into a more performant appraoch
  useEffect(() => {
    validateEmail();
    validatePassword();
    checkForErrors();
  });

  useEffect(() => {
    if (webUserInfoErrorMessage) {
      enqueueSnackbar(webUserInfoErrorMessage, { variant: 'error' });
    }

    if (email) {
      setCurrentEmailAddress(email);
    }

    setLoading(webUserInfoLoading);
  }, [webUserInfoErrorMessage, webUserInfoLoading, email, enqueueSnackbar]);

  const loanSearchInfo = useLoanSearchQuery({
    brwrEmail: email,
    includeAllBorrowers: true,
    rcsServicedOnly: false,
  });

  /**
   * Find the loans that include the current borrower email addresss
   */
  const getLoans = useCallback(async () => {
    try {
      if (loanSearchInfo.loans.length > 0) {
        let rows: SelectType[] = [];
        loanSearchInfo.loans.forEach((item) => {
          rows.push({
            selected: true,
            value: item.loanNo,
          } as SelectType);
        });
        setModalRows(rows);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }, [currentEmailAddress, enqueueSnackbar, get, token]);

  /**
   * Update the given borrower's email address.
   */
  const updateBorrowerEmail = useCallback(
    async (loanNumber: string, borrowerNumber: string, emailAddr: string) => {
      if (!token || !loanNumber || !borrowerNumber || !emailAddr) {
        return;
      }

      const formData = {
        emailAddress: emailAddr,
      };

      const url = process.env.REACT_APP_BRWR.replace('?', loanNumber) + borrowerNumber;

      const controller = get();
      const config = { signal: controller.signal };

      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };

      try {
        await axiosCall({ url: url, method: 'post', data: formData }, config, headers);
      } catch (error) {
        console.error('Unable to update Borrower Number: ' + error);
        enqueueSnackbar('Unable to update Borrower Number.', { variant: 'error' });
      }
    },
    [get, token, enqueueSnackbar],
  );

  useEffect(() => {
    if (currentEmailAddress) {
      getLoans();
    }
  }, [currentEmailAddress, getLoans]);

  return (
    <Fragment>
      {(loading || loanSearchInfo.loading) && <LoadingModal open={true} />}

      <ButtonBorderWrapper>
        <EnvelopeWithBox blockName='My Profile'>
          <div>
            <table className={Style.table}>
              <tbody>
                <tr>
                  <td>
                    <TextField
                      id='first-name'
                      label={
                        <span className={Style.disbaledTextInput}>
                          <Lock sx={{ width: '16px', height: '16px', transform: 'translateY(-1px)' }} />
                          First Name
                        </span>
                      }
                      variant='outlined'
                      value={firstName}
                      disabled={true}
                      className={Style.disbaledTextInput}
                    />
                  </td>
                  <td>
                    <TextField
                      id='last-name'
                      label={
                        <span className={Style.disbaledTextInput}>
                          <Lock sx={{ width: '16px', height: '16px', transform: 'translateY(-1px)' }} />
                          Last Name
                        </span>
                      }
                      variant='outlined'
                      value={lastName}
                      disabled={true}
                      className={Style.disbaledTextInput}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <TextField
                      id='current-email'
                      label={
                        <span className={Style.disbaledTextInput}>
                          <Lock sx={{ width: '16px', height: '16px', transform: 'translateY(-1px)' }} />
                          Current Email
                        </span>
                      }
                      variant='outlined'
                      value={currentEmailAddress}
                      disabled={true}
                      className={Style.disbaledTextInput}
                    />
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <TextField
                      id='new-email'
                      label='New Email'
                      variant='outlined'
                      value={newEmailAddress}
                      onChange={(e) => setNewEmailAddress(e.target.value)}
                      error={newEmailError}
                      helperText={newEmailErrorMessage}
                      className={Style.textInput}
                      autoComplete='new-password'
                    />
                  </td>
                  <td>
                    <TextField
                      id='confirm-new-email'
                      label='Confirm New Email'
                      variant='outlined'
                      value={confirmNewEmailAddress}
                      onChange={(e) => setConfirmNewEmailAddress(e.target.value)}
                      error={confirmNewEmailError}
                      helperText={confirmNewEmailErrorMessage}
                      className={Style.textInput}
                      autoComplete='new-password'
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <FormControl className={Style.textInput} variant='outlined'>
                      <InputLabel htmlFor='outlined-current-password'>Enter Current Password</InputLabel>
                      <OutlinedInput
                        id='outlined-current-password'
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        autoComplete='new-password'
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        error={currentPasswordError}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={handleClickShowCurrentPassword}
                              edge='end'
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                        label='Enter Current Password'
                      />
                      {currentPasswordError && (
                        <FormHelperText error>Must Enter Current Password to Update Password.</FormHelperText>
                      )}
                    </FormControl>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <FormControl className={Style.textInput} variant='outlined'>
                      <InputLabel htmlFor='outlined-new-password'>New Password</InputLabel>
                      <OutlinedInput
                        id='outlined-new-password'
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        autoComplete='new-password'
                        onChange={(e) => setNewPassword(e.target.value)}
                        error={newPasswordError}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={handleClickShowNewPassword}
                              edge='end'
                            >
                              {showNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                        label='New Password'
                      />
                      {newPasswordError && <FormHelperText error>{newPasswordErrorMessage}</FormHelperText>}
                    </FormControl>
                  </td>
                  <td>
                    <FormControl className={Style.textInput} variant='outlined'>
                      <InputLabel htmlFor='outlined-confirm-new-password'>Confirm New Password</InputLabel>
                      <OutlinedInput
                        id='outlined-confirm-new-password'
                        type={showConfirmNewPassword ? 'text' : 'password'}
                        value={confirmNewPassword}
                        autoComplete='new-password'
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        error={confirmNewPasswordError}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={handleClickShowConfirmNewPassword}
                              edge='end'
                            >
                              {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                        label='Confirm New Password'
                      />
                      {confirmNewPasswordError && (
                        <FormHelperText error>{confirmNewPasswordErrorMessage}</FormHelperText>
                      )}
                    </FormControl>
                  </td>
                </tr>
              </tbody>
            </table>
            <p className={Style.paragraph}>
              Password must include a capital letter, a lower case letter, a number, a special character (@$!%*?&), and
              be at least 8 characters long.
            </p>
            {submitLoading && <LoadingModal open={submitLoading} />}
          </div>
        </EnvelopeWithBox>
        <ButtonBorder>
          <Button
            variant='contained'
            disabled={submitLoading || disableSubmit}
            onClick={handleOpenModal}
            className={Style.button}
          >
            Submit
          </Button>
        </ButtonBorder>
      </ButtonBorderWrapper>
      <SelectModal
        open={openSelectLoanModal}
        rows={modalRows}
        handleSubmit={handleSubmitClicked}
        handleCancel={handleModalCancel}
        updateRowChecked={handleModalUpdateRowChecked}
        checkAllRows={handleModalCheckAll}
        title={modalTitle}
        columnTitle={modalLoanColumnTitle}
      />
    </Fragment>
  );
}

export default function MyProfile() {
  return (
    <SnackbarProvider maxSnack={3} preventDuplicate anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <MyProfilePage />
    </SnackbarProvider>
  );
}
